import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { TransportOrderForm } from '@forms/TransportOrderForm';
import { TransportOrderFormValues } from '@forms/TransportOrderForm/types';
import { toTransportOrderPrefill } from '@forms/TransportOrderForm/hollyPrefill';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAuthHttp, useHollySession } from '../../hooks';
import { EmailVerificationBanner } from '@components/EmailVerificationBanner';
import { toLocalDate, readBookingSource } from '@utils';
import {
  TRANSPORT_ORDER_CREATE,
  TRANSPORT_ORDER_PREPARE,
} from '../../constants';

export function CreateTransportOrder() {
  useDocumentTitle('FLOX - Create Transport Order');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authHttp = useAuthHttp();

  const supplyChainProcessId = searchParams.get('supplyChainProcessId');
  const isFromDespatchNote = !!supplyChainProcessId;
  // Holly hand-off: the user got a transport quote in the chat, signed up, and was
  // redirected here with ?source=holly. Prefill the form from their Holly session.
  // Despatch-note and Holly prefill are mutually exclusive (despatch takes priority).
  const isFromHolly = !isFromDespatchNote && searchParams.get('source') === 'holly';
  const { session: hollySession, loading: hollyLoading } = useHollySession();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!supplyChainProcessId);
  const [initialValues, setInitialValues] =
    useState<Partial<TransportOrderFormValues>>();
  const [despatchNoteContext, setDespatchNoteContext] = useState<{
    despatchNoteNumber?: string | null;
    purchaseOrderNumber?: string | null;
    collectionLat?: number | null;
    collectionLon?: number | null;
    deliveryLat?: number | null;
    deliveryLon?: number | null;
    // Per-pallet weight from the despatch note. The form's `weight` field carries the TOTAL
    // consignment weight; this separate figure feeds the network per-pallet cap gate via
    // fetchQuotes. Not user-editable, so it rides in context like the lat/lon — not a form value.
    averagePalletWeight?: number | null;
    palletTypeWasDefaulted?: boolean;
    autoSetFlags?: {
      fragile?: boolean;
    };
    dispatchCutoffTime?: string | null;
    customerOperatingHoursFrom?: string | null;
    customerOperatingHoursTo?: string | null;
    skuLines?: {
      name: string;
      quantity: number;
      palletisingType?: string | null;
    }[];
  }>({});

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Shipments', href: '/shipments', current: false },
    {
      name: 'Add Load',
      href: '/transport-order/create',
      current: true,
    },
  ];

  // Auto-fill from despatch note if supplyChainProcessId is provided
  useEffect(() => {
    if (!supplyChainProcessId) return;

    const fetchPreparedData = async () => {
      setIsLoading(true);
      try {
        const { data } = await authHttp.get(TRANSPORT_ORDER_PREPARE, {
          params: { supplyChainProcessId },
        });

        if (data?.data) {
          const prepared = data.data;
          setInitialValues({
            collectionPostcode: prepared.collectionPostcode || '',
            pickupPostcode: prepared.collectionPostcode || '',
            pickupFullAddress: prepared.collectionAddress || '',
            pickupCompanyName: prepared.collectionCompanyName || '',
            deliveryPostcode: prepared.deliveryPostcode || '',
            dropoffPostcode: prepared.deliveryPostcode || '',
            dropoffFullAddress: prepared.deliveryAddress || '',
            dropoffCompanyName: prepared.deliveryCompanyName || '',
            bookingReference: prepared.bookingReference || '',
            briefContents: prepared.briefContents || '',
            totalPallets: prepared.totalPallets || '',
            weight: prepared.totalWeight || '',
            weightUnit: prepared.weightUnit || 'Kg',
            height: prepared.maxPalletHeight || '',
            heightUnit: prepared.heightUnit || 'cm',
            // Pallet footprint from PrepareFromDespatchNote (cm). The backend is the source
            // of truth: it fills the canonical footprint for predefined pallet types and the
            // entered dims for Custom Pallet, so these are authoritative — there is no
            // client-side reconstruction (the getStandardPalletDimensions auto-fill was removed).
            palletLength: prepared.maxPalletLength || '',
            palletWidth: prepared.maxPalletWidth || '',
            // Locked field — must always have a value when sourced from a despatch note,
            // so fall back to 'Unpalletised' if the SCP doesn't carry pallet info.
            palletType: prepared.palletType || 'Unpalletised',
            packaging: prepared.packaging || '',
            deliveryDate: prepared.deliveryDateFrom?.dateTime
              ? toLocalDate(prepared.deliveryDateFrom.dateTime)
              : '',
            collectionDate: prepared.collectionDateFrom?.dateTime
              ? toLocalDate(prepared.collectionDateFrom.dateTime)
              : '',
            // Auto-set fragile / high-value handling detected from SKU data (US-004).
            // User can still toggle it off if the SKU MetaData is wrong.
            ...(prepared.requiresFragile ? { fragileHandling: true } : {}),
          });
          setDespatchNoteContext({
            despatchNoteNumber: prepared.despatchNoteNumber,
            purchaseOrderNumber: prepared.purchaseOrderNumber,
            collectionLat: prepared.collectionLat,
            collectionLon: prepared.collectionLon,
            deliveryLat: prepared.deliveryLat,
            deliveryLon: prepared.deliveryLon,
            averagePalletWeight: prepared.averagePalletWeight,
            palletTypeWasDefaulted: !prepared.palletType,
            autoSetFlags: {
              fragile: !!prepared.requiresFragile,
            },
            dispatchCutoffTime: prepared.dispatchCutoffTime ?? null,
            customerOperatingHoursFrom:
              prepared.customerOperatingHoursFrom ?? null,
            customerOperatingHoursTo: prepared.customerOperatingHoursTo ?? null,
            skuLines: Array.isArray(prepared.skuDetails)
              ? prepared.skuDetails
                  .filter((s: { name?: string | null }) => s?.name)
                  .map(
                    (s: {
                      name: string;
                      quantity: number;
                      palletisingType?: string | null;
                    }) => ({
                      name: s.name,
                      quantity: s.quantity,
                      palletisingType: s.palletisingType ?? null,
                    })
                  )
              : undefined,
          });
        }
      } catch (error) {
        console.error('Failed to fetch despatch note data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreparedData();
  }, [supplyChainProcessId]);

  // Auto-fill from the Holly chat session when arriving via ?source=holly. The
  // session fetch is handled by useHollySession; once it resolves we map the flat
  // v3 flowState onto form values. Partial prefill — addresses/contacts stay blank.
  useEffect(() => {
    if (!isFromHolly) return;
    if (hollyLoading) return;
    setInitialValues(toTransportOrderPrefill(hollySession?.flowState));
  }, [isFromHolly, hollyLoading, hollySession]);

  const handleOnSubmit = async (formData: TransportOrderFormValues) => {
    setIsSubmitting(true);
    try {
      await authHttp.post(TRANSPORT_ORDER_CREATE, {
        transportOrder: {
          supplyChainProcessId: supplyChainProcessId || undefined,
          collectionPostcode: formData.collectionPostcode,
          collectionAddress: formData.pickupFullAddress,
          collectionCompanyName: formData.pickupCompanyName,
          collectionContactName: formData.pickupContactName,
          collectionContactPhone: formData.pickupContactNumber,
          collectionContactEmail: formData.pickupContactEmail,
          deliveryPostcode: formData.deliveryPostcode,
          deliveryAddress: formData.dropoffFullAddress,
          deliveryCompanyName: formData.dropoffCompanyName,
          deliveryContactName: formData.dropoffContactName,
          deliveryContactPhone: formData.dropoffContactNumber,
          deliveryContactEmail: formData.dropoffContactEmail,
          totalPallets: formData.totalPallets
            ? Number(formData.totalPallets)
            : null,
          palletType: formData.palletType || null,
          stackable: formData.stackable,
          weight: formData.weight ? Number(formData.weight) : null,
          weightUnit: formData.weightUnit || null,
          height: formData.height ? Number(formData.height) : null,
          heightUnit: formData.heightUnit || null,
          briefContents: formData.briefContents || null,
          preferredVehicle: formData.preferredVehicle || null,
          curtainSideOrBoxBody: formData.curtainSideOrBoxBody || null,
          tailLiftRequired: formData.tailLiftRequired,
          moffettRequired: formData.moffettRequired,
          minimumInternalHeight: formData.minimumInternalHeight
            ? Number(formData.minimumInternalHeight)
            : null,
          forkliftAtCollection: formData.forkliftAtCollection,
          forkliftAtDelivery: formData.forkliftAtDelivery,
          dockLoading: formData.dockLoading,
          handballRequired: formData.handballRequired,
          palletTruckNeeded: formData.palletTruckNeeded,
          insuranceCover: formData.insuranceCover
            ? Number(formData.insuranceCover)
            : null,
          insuranceCurrency: formData.insuranceCurrency || null,
          sealNumbers: formData.sealNumbers || null,
          fragileHandling: formData.fragileHandling,
          bookingReference: formData.bookingReference || null,
          notes: formData.pickupAdditionalNotes || null,
          // Selected quote — all optional; null when the user took the manual-quote path.
          selectedRateCardId: formData.selectedRateCardId || null,
          selectedQuoteSource: formData.selectedQuoteSource || null,
          selectedQuoteServiceName: formData.selectedQuoteServiceName || null,
          quotedPrice:
            typeof formData.quotedPrice === 'number'
              ? formData.quotedPrice
              : null,
          quotedCurrency: formData.quotedCurrency || null,
          quotedAt: formData.quotedAt || null,
          quotedRateCardSnapshot: formData.quotedRateCardSnapshot || null,
          // Full list of quotes shown to the user, for TransportOrderCreated audit (structured
          // List<QuoteOption> on the event). Backend identifies the selected one via
          // selectedRateCardId above.
          shownQuotes: formData.shownQuotes ?? [],
          bookingSource: readBookingSource(),
        },
      });

      navigate('/noticeboard');
    } catch (error) {
      console.error('Error creating transport order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gate the form mount until prefill source resolves — RHF applies defaultValues
  // at mount, so initialValues must be ready first. For the Holly path,
  // `hollyLoading` flips false one render BEFORE the effect below populates
  // `initialValues`; if we mounted on that render the form would capture
  // undefined defaults and ignore the later update. Hold the spinner until
  // `initialValues` is actually set (the effect always assigns at least `{}`).
  if (
    isLoading ||
    (isFromHolly && (hollyLoading || initialValues === undefined))
  ) {
    return (
      <div className='flex items-center justify-center h-[calc(100vh-12rem)]'>
        <div className='flex flex-col items-center gap-3'>
          <FloxSpinner size='md' />
          <p className='text-gray-500 text-sm'>
            {isFromHolly ? 'Loading your quote details...' : 'Loading despatch note data...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='px-4 lg:px-8 pt-4'>
        <EmailVerificationBanner />
      </div>
      <TransportOrderForm
        onSubmit={handleOnSubmit}
        isSubmitting={isSubmitting}
        header='Transport Order'
        pages={pages}
        initialValues={initialValues}
        isFromDespatchNote={isFromDespatchNote}
        isFromHolly={isFromHolly}
        supplyChainProcessId={supplyChainProcessId}
        despatchNoteNumber={despatchNoteContext.despatchNoteNumber}
        purchaseOrderNumber={despatchNoteContext.purchaseOrderNumber}
        collectionLat={despatchNoteContext.collectionLat}
        collectionLon={despatchNoteContext.collectionLon}
        deliveryLat={despatchNoteContext.deliveryLat}
        deliveryLon={despatchNoteContext.deliveryLon}
        averagePalletWeight={despatchNoteContext.averagePalletWeight}
        palletTypeWasDefaulted={despatchNoteContext.palletTypeWasDefaulted}
        autoSetFlags={despatchNoteContext.autoSetFlags}
        dispatchCutoffTime={despatchNoteContext.dispatchCutoffTime}
        customerOperatingHoursFrom={
          despatchNoteContext.customerOperatingHoursFrom
        }
        customerOperatingHoursTo={despatchNoteContext.customerOperatingHoursTo}
        skuLines={despatchNoteContext.skuLines}
      />
    </>
  );
}
